import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { AvailableNotificationViewModel } from "client/Api/Notifications/notifications";
import { AnimatedStackFooterKind } from "./Stack/AnimatedStack";
import { ActivityStackState, IActivityStack } from "./stacks";
import { markAsOrdered, OrderedReadonlyArray } from "./store";

export enum ActivityFeedItemKind {
	Activity = 0,
	AnimatedStack = 1,
	ExpandedStackFooter = 2
};

export type ActivityFeedItemModel = {
	key: string;
	isPrerender: boolean;
	kind: ActivityFeedItemKind.Activity;
	activity: AvailableNotificationViewModel;
};

export type AnimatedStackFeedItemModel = {
	key: string;
	isPrerender: false;
	kind: ActivityFeedItemKind.AnimatedStack;
	stackId: string;
	canBeDeletedByUser: boolean;
	title: string;
	activities: OrderedReadonlyArray<AvailableNotificationViewModel>;
	totalNumberOfActivities: number;
	footerKind: AnimatedStackFooterKind;
	isStackExpanded: boolean;
	hasReducedPaddingAfterExpansion: boolean;
};

export type ExpandedStackFooterFeedItemModel = {
	key: string;
	animatedItem: GeneralFeedItemModel;
	isPrerender: boolean;
	kind: ActivityFeedItemKind.ExpandedStackFooter;
	shouldRenderButtons: boolean;
	canBeDeletedByUser: boolean;
	stackId: string;
};

export type GeneralFeedItemModel =
	| ActivityFeedItemModel
	| AnimatedStackFeedItemModel
	| ExpandedStackFooterFeedItemModel;

export function flattenActivityStacks(
	items: OrderedReadonlyArray<AvailableNotificationViewModel|IActivityStack>,
	stacksState: Map<string, ActivityStackState>
): OrderedReadonlyArray<GeneralFeedItemModel> {
	const result: GeneralFeedItemModel[] = [];

	let expandedStackShouldHaveReducedPaddingTop: boolean = true;

	for (const item of items) {
		if (isStack(item)) {
			const stackState: ActivityStackState = stacksState.get(item.id) || "collapsed";
			result.push(...getStackItems(item, stackState, expandedStackShouldHaveReducedPaddingTop));
			expandedStackShouldHaveReducedPaddingTop = stackState === "expanded" || stackState === "expanding";
		} else {
			result.push(getActivityFeedItem(item, false));
			expandedStackShouldHaveReducedPaddingTop = false;
		}
	}

	return markAsOrdered({ alreadyOrderedItems: result });
};

function getStackItems(
	stack: IActivityStack,
	stackState: ActivityStackState,
	hasReducedPaddingTop: boolean
): ReadonlyArray<GeneralFeedItemModel> {
	switch (stackState) {
		case "collapsed":
			return [
				getAnimatedStackItem(
					stack,
					{
						isExpanded: false,
						hasReducedPaddingTop
					}
				)
			];
		case "expanding":
			const expandingAnimatedItem: GeneralFeedItemModel = getAnimatedStackItem(stack, { isExpanded: true, hasReducedPaddingTop });
			return [
				...getNonAnimatedItems(
					stack,
					expandingAnimatedItem,
					{ isPrerender: true }
				),
				expandingAnimatedItem
			];
		case "expanded":
			const expandedAnimatedItem: GeneralFeedItemModel = getAnimatedStackItem(stack, { isExpanded: true, hasReducedPaddingTop });
			return [
				expandedAnimatedItem,
				...getNonAnimatedItems(
					stack,
					expandedAnimatedItem,
					{ isPrerender: false }
				)
			];
		default:
			throw new UnreachableCaseError(stackState);
	}
}

const MAX_NUMBER_OF_ITEMS_TO_ANIMATE = 15;
const MIN_NUMBER_OF_ACTIVITIES_FOR_FOOTER = 5;

function getAnimatedStackItem(
	stack: IActivityStack,
	{ isExpanded, hasReducedPaddingTop }: { isExpanded: boolean; hasReducedPaddingTop: boolean; }
): GeneralFeedItemModel {

	const activitiesToAnimate: OrderedReadonlyArray<AvailableNotificationViewModel> =
		markAsOrdered(
			{ alreadyOrderedItems: stack.activities.slice(0, Math.min(MAX_NUMBER_OF_ITEMS_TO_ANIMATE, stack.activities.length)) }
		);

	const shouldRenderFooterAsSeparateItem: boolean = stack.activities.length > MAX_NUMBER_OF_ITEMS_TO_ANIMATE;

	const footerKind: AnimatedStackFooterKind = shouldRenderFooterAsSeparateItem
		? "none"
		: stack.activities.length >= MIN_NUMBER_OF_ACTIVITIES_FOR_FOOTER
			? "full"
			: "only-padding";

	return {
		key: generateAnimatedStackKey(stack.id),
		isPrerender: false,
		kind: ActivityFeedItemKind.AnimatedStack,
		isStackExpanded: isExpanded,
		title: stack.title,
		hasReducedPaddingAfterExpansion: hasReducedPaddingTop,
		activities: activitiesToAnimate,
		canBeDeletedByUser: canStackBeDeletedByUser(stack),
		footerKind,
		stackId: stack.id,
		totalNumberOfActivities: stack.activities.length
	};
}

function getNonAnimatedItems(
	stack: IActivityStack,
	animatedItem: GeneralFeedItemModel,
	{ isPrerender }: { isPrerender: boolean; }
): ReadonlyArray<GeneralFeedItemModel> {

	if (stack.activities.length <= MAX_NUMBER_OF_ITEMS_TO_ANIMATE) {
		return [];
	}

	const shouldRenderButtonsInFooter: boolean = stack.activities.length >= MIN_NUMBER_OF_ACTIVITIES_FOR_FOOTER;

	const activitiesToRender: ReadonlyArray<AvailableNotificationViewModel> = stack.activities
		.slice(MAX_NUMBER_OF_ITEMS_TO_ANIMATE, stack.activities.length);

	return [
		...activitiesToRender.map(a => getActivityFeedItem(a, isPrerender)),
		{
			key: `expandedStackFooter-${stack.id}`,
			animatedItem,
			isPrerender,
			kind: ActivityFeedItemKind.ExpandedStackFooter,
			stackId: stack.id,
			shouldRenderButtons: shouldRenderButtonsInFooter,
			canBeDeletedByUser: canStackBeDeletedByUser(stack)
		}
	];
}

function getActivityFeedItem(
	activity: AvailableNotificationViewModel,
	isPrerender: boolean
): ActivityFeedItemModel {
	return {
		key: `activity-${activity.id}`,
		isPrerender,
		kind: ActivityFeedItemKind.Activity,
		activity
	};
}

function canStackBeDeletedByUser(stack: IActivityStack): boolean {
	return stack.activities.some(a => a.canBeDeletedByUser);
}

function generateAnimatedStackKey(stackId: string) {
	return `animatedStack-${stackId}`;
}

export function isStack(activityOrStack: AvailableNotificationViewModel | IActivityStack): activityOrStack is IActivityStack {
	return typeof (activityOrStack as IActivityStack).activities === "object";
}